import { useEffect } from "react";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { useT } from "../hooks/useLang";
import { APP_VERSION_LABEL } from "../lib/version";

interface Props {
  open: boolean;
  onClose: () => void;
  onOpenResearch: () => void;
  onOpenCatalog: () => void;
  onOpenAtlas: () => void;
}

/**
 * About this version — the public door to the research paper and the data sets.
 * Operators stay on the map. Anyone who wants the paper or the source list
 * can open it from here without hunting for WP / ATLAS / SOURCES.
 */
export function AboutSheet({ open, onClose, onOpenResearch, onOpenCatalog, onOpenAtlas }: Props) {
  const t = useT();
  const containerRef = useFocusTrap(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="manual-backdrop" onClick={onClose}>
      <div
        ref={containerRef}
        className="manual about-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={t("About this version", "เกี่ยวกับรุ่นนี้")}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="manual-head">
          <div className="col">
            <span className="eyebrow mono">YCM-01 · {t("Yala City Municipality", "เทศบาลนครยะลา")}</span>
            <h2 className="about-version">{APP_VERSION_LABEL}</h2>
            <span className="caption" style={{ color: "var(--text-2)" }}>
              {t(
                "Flood-first control tower. This sheet is the way into the research and the data.",
                "หอควบคุมที่น้ำท่วมมาก่อน หน้านี้คือทางเข้างานวิจัยและชุดข้อมูล",
              )}
            </span>
          </div>
          <button type="button" onClick={onClose} className="mono manual-close" aria-label={t("Close about", "ปิด")}>
            [ESC] CLOSE
          </button>
        </header>

        <div className="manual-body">
          <section className="manual-section">
            <h3 className="manual-h3">{t("About", "เกี่ยวกับ")}</h3>
            <p>
              {t(
                "Built for the duty officer and the Mayor of Yala. Open the FLOOD lens to read the Pattani River gauges beside the five municipal flood cameras: Tha Sap bridge (river side and municipal side), the new-market pump, Jaru, and the Baan Rom shelter. A camera marked dark is a camera that is not reporting.",
                "ทำไว้สำหรับเจ้าหน้าที่เวรและนายกเทศมนตรีนครยะลา เปิดเลนส์ FLOOD เพื่ออ่านมาตรวัดแม่น้ำปัตตานีคู่กับกล้องเทศบาล 5 ตัว: สะพานท่าสาปฝั่งแม่น้ำและฝั่งเทศบาล ตลาดเมืองใหม่ จารู และบ้านร่ม กล้องที่มืดคือกล้องที่ไม่ส่งสัญญาณ",
              )}
            </p>
            <p>
              {t(
                "Every panel shows how old its data is. OFFLINE means that feed is down. It does not mean the city is fine. Thai is the default. English is the language toggle. The sun, moon, and half-circle in the header cycle light, night, and high-contrast.",
                "ทุกแผงบอกว่าข้อมูลเก่าแค่ไหน OFFLINE หมายถึงฟีดนั้นดับ ไม่ได้แปลว่าเมืองปกติ ภาษาไทยเป็นค่าเริ่มต้น ปุ่มภาษาสลับเป็นอังกฤษ ดวงอาทิตย์ พระจันทร์ และวงครึ่งในหัวเว็บสลับโหมดสว่าง กลางคืน และคอนทราสต์สูง",
              )}
            </p>
          </section>

          <section className="manual-section">
            <h3 className="manual-h3">{t("Research", "งานวิจัย")}</h3>
            <p>
              {t(
                "The bilingual paper: why this tower exists, which risks it is built around, and how a feed earns the word live.",
                "เอกสารสองภาษา: หอควบคุมนี้มีไว้ทำไม สร้างรอบความเสี่ยงใด และฟีดต้องทำอย่างไรจึงจะเรียกว่าสดได้",
              )}
            </p>
            <button type="button" className="about-action" onClick={onOpenResearch}>
              {t("Open the research paper", "เปิดเอกสารงานวิจัย")}
            </button>
          </section>

          <section className="manual-section">
            <h3 className="manual-h3">{t("Data sets", "ชุดข้อมูล")}</h3>
            <p>
              {t(
                "The source catalog is what is connected in this version, with freshness on each row. The atlas is the outcome indicators — poverty, education, health, climate — and the longer source bible.",
                "บัญชีแหล่งข้อมูลคือสิ่งที่เชื่อมอยู่ในรุ่นนี้ พร้อมความสดของแต่ละแถว แผนที่ข้อมูลคือตัวชี้วัดผลลัพธ์ ความยากจน การศึกษา สุขภาพ ภูมิอากาศ และบัญชีแหล่งข้อมูลฉบับยาว",
              )}
            </p>
            <div className="about-actions">
              <button type="button" className="about-action" onClick={onOpenCatalog}>
                {t("Open the source catalog", "เปิดบัญชีแหล่งข้อมูล")}
              </button>
              <button type="button" className="about-action" onClick={onOpenAtlas}>
                {t("Open the data atlas", "เปิดแผนที่ข้อมูล")}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
